import React, { useCallback } from 'react';
import { Button, Popconfirm } from 'antd';
import type { FH113 } from '../../../services/pwh201Api';
import { dateToInput, inputToDate, safeFloat, fmt } from '../helpers';

interface Tab3Props {
  invoices: Partial<FH113>[];
  isEditing: boolean;
  isLocked: boolean;
  masterNo: string;
  onInvoicesChange: (invoices: Partial<FH113>[]) => void;
}

const Tab3InvoiceDetail: React.FC<Tab3Props> = React.memo(({
  invoices, isEditing, isLocked, masterNo, onInvoicesChange,
}) => {
  const disabled = !isEditing || isLocked;

  const handleAdd = useCallback(() => {
    const seq = invoices.length + 1;
    const newRow: Partial<FH113> = {
      fh113_01: masterNo,
      fh113_02: seq,
      fh113_11: '',
      fh113_12: null,
      fh113_13: 0,
      fh113_14: 0,
      fh113_15: 0,
      fh113_16: 0,
      fh113_17: 0,
      fh113_18: 0,
      fh113_19: false,
      fh113_90: 0,
    };
    onInvoicesChange([...invoices, newRow]);
  }, [invoices, masterNo, onInvoicesChange]);

  const handleDelete = useCallback((idx: number) => {
    const updated = invoices.filter((_, i) => i !== idx).map((row, i) => ({
      ...row,
      fh113_02: i + 1,
    }));
    onInvoicesChange(updated);
  }, [invoices, onInvoicesChange]);

  const handleFieldChange = useCallback((idx: number, field: keyof FH113, value: unknown) => {
    const updated = [...invoices];
    updated[idx] = { ...updated[idx], [field]: value };
    // Auto-calc total: net + tax
    if (field === 'fh113_13' || field === 'fh113_14') {
      const net = field === 'fh113_13' ? (value as number) : (updated[idx].fh113_13 || 0);
      const tax = field === 'fh113_14' ? (value as number) : (updated[idx].fh113_14 || 0);
      updated[idx].fh113_15 = net + tax;
    }
    onInvoicesChange(updated);
  }, [invoices, onInvoicesChange]);

  // Totals
  const totalNet = invoices.reduce((s, r) => s + (r.fh113_13 || 0), 0);
  const totalTax = invoices.reduce((s, r) => s + (r.fh113_14 || 0), 0);
  const totalGross = invoices.reduce((s, r) => s + (r.fh113_15 || 0), 0);
  const totalReqNet = invoices.reduce((s, r) => s + (r.fh113_16 || 0), 0);
  const totalReqTax = invoices.reduce((s, r) => s + (r.fh113_17 || 0), 0);

  return (
    <div className="pwh201-tab-content">
      <div className="pwh201-detail-toolbar">
        <span className="pwh201-section-title">請款明細（發票）</span>
        {!disabled && (
          <>
            <Button size="small" type="primary" onClick={handleAdd}>明細新增</Button>
          </>
        )}
        <span className="pwh201-detail-count">總項次: {invoices.length}</span>
      </div>

      <div className="pwh201-detail-grid">
        <table>
          <thead>
            <tr>
              <th data-contract-field="fh113_02">項次</th>
              <th data-contract-field="fh113_11">發票號碼</th>
              <th data-contract-field="fh113_12">發票日期</th>
              <th data-contract-field="fh113_13">發票淨額</th>
              <th data-contract-field="fh113_14">發票稅額</th>
              <th data-contract-field="fh113_15">發票總額</th>
              <th data-contract-field="fh113_16">未稅請款金額</th>
              <th data-contract-field="fh113_17">請款稅額</th>
              {!disabled && <th data-acceptance-non-field="row-action">操作</th>}
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr>
                <td colSpan={disabled ? 8 : 9} className="pwh201-empty-row">
                  尚無明細資料
                </td>
              </tr>
            )}
            {invoices.map((row, idx) => (
              <tr key={idx}>
                <td data-contract-field="fh113_02">{row.fh113_02}</td>
                <td>
                  <input
                    data-contract-field="fh113_11"
                    value={row.fh113_11 || ''}
                    onChange={e => handleFieldChange(idx, 'fh113_11', e.target.value)}
                    disabled={disabled}
                    maxLength={20}
                    className="grid-input"
                  />
                </td>
                <td>
                  <input
                    data-contract-field="fh113_12"
                    type="date"
                    value={dateToInput(row.fh113_12)}
                    onChange={e => handleFieldChange(idx, 'fh113_12', inputToDate(e.target.value))}
                    disabled={disabled}
                    className="grid-input date-input"
                  />
                </td>
                <td>
                  <input
                    data-contract-field="fh113_13"
                    type="number"
                    value={row.fh113_13 ?? ''}
                    onChange={e => handleFieldChange(idx, 'fh113_13', safeFloat(e.target.value))}
                    disabled={disabled}
                    className="grid-input amount-input"
                  />
                </td>
                <td>
                  <input
                    data-contract-field="fh113_14"
                    type="number"
                    value={row.fh113_14 ?? ''}
                    onChange={e => handleFieldChange(idx, 'fh113_14', safeFloat(e.target.value))}
                    disabled={disabled}
                    className="grid-input amount-input"
                  />
                </td>
                <td className="amount-cell" data-contract-field="fh113_15">{fmt(row.fh113_15)}</td>
                <td>
                  <input
                    data-contract-field="fh113_16"
                    type="number"
                    value={row.fh113_16 ?? ''}
                    onChange={e => handleFieldChange(idx, 'fh113_16', safeFloat(e.target.value))}
                    disabled={disabled}
                    className="grid-input amount-input"
                  />
                </td>
                <td>
                  <input
                    data-contract-field="fh113_17"
                    type="number"
                    value={row.fh113_17 ?? ''}
                    onChange={e => handleFieldChange(idx, 'fh113_17', safeFloat(e.target.value))}
                    disabled={disabled}
                    className="grid-input amount-input"
                  />
                </td>
                {!disabled && (
                  <td>
                    <Popconfirm
                      title="確認刪除此筆明細？"
                      onConfirm={() => handleDelete(idx)}
                      okText="確認"
                      cancelText="取消"
                    >
                      <Button size="small" danger>刪除</Button>
                    </Popconfirm>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan={3}>合計</td>
              <td className="amount-cell">{fmt(totalNet)}</td>
              <td className="amount-cell">{fmt(totalTax)}</td>
              <td className="amount-cell">{fmt(totalGross)}</td>
              <td className="amount-cell">{fmt(totalReqNet)}</td>
              <td className="amount-cell">{fmt(totalReqTax)}</td>
              {!disabled && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
});

Tab3InvoiceDetail.displayName = 'Tab3InvoiceDetail';
export default Tab3InvoiceDetail;
